import { useUserContext } from '@/contexts/UserContext';
import { useRef } from 'react';
import { AgentChatProvider } from '../../contexts/AgentChatContext';
import { HtmlStepperUpdaterProvider } from '../../contexts/HtmlStepperUpdaterContext';
import { useSystemConfig } from '../../contexts/SystemConfigContext';
import { MaintenanceOverlay } from '../Common/MaintenanceOverlay';
import { HtmlStepperEditorProvider } from './HtmlStepperEditorContext';
import { HtmlStepperEditorHeader } from './HtmlStepperEditorHeader';
import { HtmlStepperEditorMainContent } from './HtmlStepperEditorMainContent';

interface HtmlStepperEditorProps {
  quizId: string;
  initialFormId: string;
  onClose: () => void;
}

// Internal component that uses the agent context
function HtmlStepperEditorInner({
  quizId,
  initialFormId,
  onClose,
}: HtmlStepperEditorProps) {
  const previewRef = useRef<HTMLIFrameElement>(null);
  const { getFeatureMaintenanceConfig } = useSystemConfig();
  const { selectedAccount } = useUserContext();

  // Check if maintenance mode is enabled (helper handles support admin check)
  const maintenanceConfig = getFeatureMaintenanceConfig('htmlStepperEditor');

  if (!selectedAccount) {
    return null;
  }

  return (
    <HtmlStepperUpdaterProvider accountId={selectedAccount.id} quizId={quizId}>
      <HtmlStepperEditorProvider
        quizId={quizId}
        initialFormId={initialFormId}
        onClose={onClose}
        previewRef={previewRef}
      >
        <div className="absolute inset-0 bg-gray-50">
          {/* Header */}
          <HtmlStepperEditorHeader />

          {/* Main Content */}
          <HtmlStepperEditorMainContent />

          {/* Maintenance Overlay */}
          {maintenanceConfig && (
            <MaintenanceOverlay config={maintenanceConfig} onClose={onClose} />
          )}
        </div>
      </HtmlStepperEditorProvider>
    </HtmlStepperUpdaterProvider>
  );
}

// Main component with AgentChatProvider wrapper
export default function HtmlStepperEditor(props: HtmlStepperEditorProps) {
  return (
    <AgentChatProvider agentName="htmlStepperFormAgent">
      <HtmlStepperEditorInner {...props} />
    </AgentChatProvider>
  );
}
